import { Toast } from '@components/Toast';
import { faCloudExclamation, faRight } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useListLanguages } from '@partful/features/instructions/languages/lib/useListLanguages';
import { ErrorMessage } from '@partful/features/instructions/ui/ErrorMessage';
import { useCreateVersionLanguage } from '@partful/features/instructions/version-languages/lib/useCreateVersionLanguage';
import { VersionModel } from '@partful/instructions-domain-models';
import { classNames } from '@partful/shared/lib/classNames';
import { Autocomplete } from '@partful/shared/ui/Autocomplete';
import { Button } from '@partful/shared/ui/Button';
import { Typography } from '@partful/shared/ui/Typography';
import { ComponentProps, FormEventHandler, forwardRef, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './DefaultLanguageForm.module.scss';

type DefaultLanguageFormProps = {
  version: VersionModel;
  disabled?: boolean;
} & Omit<ComponentProps<'form'>, 'onSubmit'>;

export const DefaultLanguageForm = forwardRef<HTMLFormElement, DefaultLanguageFormProps>(
  ({ version, disabled, className, ...rest }, ref) => {
    const { t: i18n } = useTranslation();

    const [selectedLanguage, setSelectedLanguage] = useState<string | null>(null);

    const { data: languageData, isLoading: isLanguagesLoading, isError: isLanguagesError } = useListLanguages();

    const languages = useMemo(
      () =>
        languageData?.sort((a, b) => {
          const i18nA = i18n(`languageNames:${a.code}`);
          const i18nB = i18n(`languageNames:${b.code}`);
          return i18nA.localeCompare(i18nB);
        }),
      [i18n, languageData]
    );

    const {
      mutate: createVersionLanguage,
      isLoading: isAddingDefaultLanguageLoading,
      isError: isAddingDefaultLanguageError,
      isSuccess: isAddingDefaultLanguageSuccess,
    } = useCreateVersionLanguage();

    const handleAddDefaultLanguage: FormEventHandler<HTMLFormElement> = (e) => {
      e.preventDefault();
      if (version && selectedLanguage) {
        createVersionLanguage({
          instructionId: version.instructionId,
          versionId: version.id,
          args: {
            isDefault: true,
            languageCode: selectedLanguage,
          },
        });
      }
    };

    if (isLanguagesError) {
      return (
        <div className={styles.error}>
          <ErrorMessage
            icon={<FontAwesomeIcon icon={faCloudExclamation} />}
            message={i18n('instructions:manage.version.instructions.loadFailed.message')}
            title={i18n('instructions:manage.version.instructions.loadFailed.title')}
          />
        </div>
      );
    }

    return (
      <form className={classNames(styles.form, className)} {...rest} onSubmit={handleAddDefaultLanguage} ref={ref}>
        <div>
          <Typography htmlFor="defaultLanguageDescription">
            {i18n('instructions:manage.version.instructions.defaultLanguageDescription')}
          </Typography>
        </div>
        <div className={classNames(styles.control, styles.languageControl)}>
          <Typography as="label" htmlFor="defaultLanguage" fontWeight={600}>
            {i18n('instructions:version.data.defaultLanguage.label')}
            <sup>({i18n('instructions:common.required')})</sup>
          </Typography>
          <Autocomplete
            data-testid="defaultLanguage"
            placeholder={i18n('instructions:manage.version.instructions.defaultLanguagePlaceholder')}
            disabled={isLanguagesError || isLanguagesLoading}
            size="medium"
            value={selectedLanguage}
            onChange={(e, value) => {
              setSelectedLanguage(value as string);
            }}
            autoComplete
            autoSelect
            getOptionLabel={(value) => i18n(`languageNames:${value}`)}
            options={(languages ?? []).map((language) => language.code)}
          />
        </div>
        <div>
          <Button
            type="submit"
            data-testid="add-default-language"
            size="large"
            disabled={isAddingDefaultLanguageLoading || isAddingDefaultLanguageError || !selectedLanguage}
          >
            {i18n('instructions:version.actions.addLanguage')}
            <FontAwesomeIcon icon={faRight} />
          </Button>
        </div>
        <Toast
          dependency={isAddingDefaultLanguageError}
          severity="error"
          title={i18n('instructions:version.actions.addDefaultLanguageError.title', {
            languageName: i18n(`languageNames:${selectedLanguage}`),
            versionNumber: version.versionNumber,
          })}
          testId="default-language-error-message"
        />
        <Toast
          dependency={isAddingDefaultLanguageSuccess}
          severity="success"
          title={i18n('instructions:version.actions.addDefaultLanguageSuccess.title', {
            languageName: i18n(`languageNames:${selectedLanguage}`),
            versionNumber: version.versionNumber,
          })}
          testId="default-language-success-message"
        />
      </form>
    );
  }
);
